import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    CredentialStoreGenericType,
    ICredentialStore,
    ICredentialStoreConfigurationEntry,
    ICredentialStoreType,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { CredentialStoresService } from '@services';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest';
import UiValidators, { CREDENTIAL_STORES_NAME_MAX_LENGTH } from '@validators';

import {
    BehaviorSubject,
    iif,
    of,
    Subject,
} from 'rxjs';
import {
    finalize,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { DynamicConfigurationFormService } from '../dynamic-configuration-form';

enum CredentialStoreControl {
    type = 'type',
    name = 'name',
    additionalConfiguration = 'additionalConfiguration',
}

enum SecureCredentialStoreControl {
    safe = 'safe',
    folder = 'folder',
    applicationId = 'applicationId',
}

@Component({
    templateUrl: './credential-store.modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CredentialStoresService],
})
export class CredentialStoreModalComponent extends UiModalComponent<ICredentialStore>
    implements OnInit, OnDestroy {

    public form!: FormGroup;
    public CredentialStoreControl = CredentialStoreControl;
    public SecureCredentialStoreControl = SecureCredentialStoreControl;
    public CredentialStoreGenericType = CredentialStoreGenericType;
    public orchestratorDatabaseName!: string;

    public secureStoreConfiguration!: ICredentialStoreConfigurationEntry[];

    public loading$ = new BehaviorSubject<boolean>(false);
    public loadingData$ = new BehaviorSubject<boolean>(false);

    public loadingDynamicForm$ = new BehaviorSubject<boolean>(false);

    public storeTypeDropdownEntries: ISuggestValueData<CredentialStoreGenericType>[] = [];

    private _formBuilder = this._injector.get<FormBuilder>(FormBuilder);
    private _credentialStoreService = this._injector.get<CredentialStoresService>(CredentialStoresService);
    private _dynamicConfigurationFormService = this._injector.get<DynamicConfigurationFormService>(DynamicConfigurationFormService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    private _destroyed$ = new Subject<void>();

    private _defaultModel!: ICredentialStore;

    private get _defaultFormGroupConfig() {
        return {
            [CredentialStoreControl.type]: [],
            [CredentialStoreControl.name]: [
                this._defaultModel.name,
                Validators.compose([
                    UiValidators.required({
                        allowWhitespace: false,
                    }),
                    Validators.maxLength(CREDENTIAL_STORES_NAME_MAX_LENGTH),
                ]),
            ],
        };
    }

    private get _formValue(): ICredentialStore {
        return {
            ...this.form.value,
            name: this.form.get('name')!.value.trim(),
            id: this._defaultModel.id,
            type: {
                value: this.selectedType.id,
                genericType: this.selectedType.data,
            },
        };
    }

    public get additionalConfigurationFormGroup() {
        return this.form.get(CredentialStoreControl.additionalConfiguration);
    }

    public get selectedType(): ISuggestValueData<CredentialStoreGenericType> {
        const [selected] = this.form.get(CredentialStoreControl.type)!.value as ISuggestValueData<CredentialStoreGenericType>[];
        return selected;
    }

    ngOnInit() {
        this.loadingData$.next(true);

        this.orchestratorDatabaseName = this._translateService.instant('credential_stores_orchestrator_database');

        this._defaultModel = !this.context.isNewEntry ?
            this.context.data : {
                additionalConfiguration: {},
            } as ICredentialStore;

        this.form = this._formBuilder.group(this._defaultFormGroupConfig);

        this.form
            .get(CredentialStoreControl.type)!
            .valueChanges
            .pipe(
                tap(() => this.loadingDynamicForm$.next(true)),
                switchMap(([{ data, id }]: ISuggestValueData<CredentialStoreGenericType>[]) => {
                    if (data === CredentialStoreGenericType.Database) {
                        return of(null);
                    }

                    return this._credentialStoreService
                        .getSecureCredentialStoreConfiguration(id as string);
                }),
            )
            .subscribe(
                (value) => {
                    this._updateFormFields(value);
                    this.loadingDynamicForm$.next(false);
                },
            );

        iif(
            () => this.context.isNewEntry,
            this._credentialStoreService.getCredentialStoreTypes(),
            of(null),
        ).pipe(
            finalize(() => this.loadingData$.next(false)),
        ).subscribe(
            credentialStoreTypes => {
                const storeTypeToDropdownEntry = (storeType: ICredentialStoreType) => ({
                    id: storeType.value,
                    text: storeType.value,
                    data: storeType.genericType,
                });

                if (!credentialStoreTypes) {
                    const storeType = storeTypeToDropdownEntry(this.context.data.type);

                    this.storeTypeDropdownEntries = [storeType];
                    this.form.get(CredentialStoreControl.type)!.setValue([storeType]);

                    return;
                }

                this.storeTypeDropdownEntries = credentialStoreTypes
                    .map(storeTypeToDropdownEntry);

                const [firstAvailableStore] = this.storeTypeDropdownEntries;
                this.form.get(CredentialStoreControl.type)!.setValue([firstAvailableStore]);
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public onSubmit() {
        this.loading$.next(true);

        iif(
            () => this.context.isNewEntry,
            this._credentialStoreService.add(this._formValue),
            this._credentialStoreService.update(this._defaultModel.id, this._formValue),
        ).pipe(
            finalize(
                () => this.loading$.next(false),
            ),
            takeUntil(this._destroyed$),
        ).subscribe(() => {
            this._dialogRef.close({
                type: ModalResultType.Succeeded,
            });
        });
    }

    /**
     *
     * @param secureStoreConfiguration its null when the form type is a db form.
     */
    private _updateFormFields(secureStoreConfiguration: ICredentialStoreConfigurationEntry[] | null) {
        this.form.removeControl(CredentialStoreControl.additionalConfiguration);
        this.secureStoreConfiguration = [];

        if (secureStoreConfiguration === null) {
            this.form.get(CredentialStoreControl.name)!.setValue(this.orchestratorDatabaseName);
        } else {
            this.secureStoreConfiguration = secureStoreConfiguration;

            const dynamicFormConfiguration = this._dynamicConfigurationFormService.toFormGroup(
                this.secureStoreConfiguration,
                this._defaultModel.additionalConfiguration,
            );

            this.form.addControl(
                CredentialStoreControl.additionalConfiguration,
                dynamicFormConfiguration,
            );

            this.form.get(CredentialStoreControl.name)!.setValue(this._defaultModel.name);
        }
    }
}
